import React, { useEffect, useRef } from 'react';
import i18n from '../../translation/i18n';

const LANG_TO_LOCALE: Record<string, string> = {
  en: 'en-GB',
  pt: 'pt-PT',
};

interface FloatingWidgetProps {
  embedCode: string;
}

const BK_SELECTOR =
  'bk-floating-widget, [class*="bk-"], [id*="bookingkit"], [class*="bookingkit"]';

/**
 * Renders a BookingKit floating widget from a raw embed code snippet.
 * Scripts injected via innerHTML don't execute, so we parse them out
 * and append them as proper script elements.
 */
const FloatingWidget = ({ embedCode }: FloatingWidgetProps): React.ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Parse the embed code
    const template = document.createElement('template');
    template.innerHTML = embedCode.trim();

    // Separate scripts from other elements
    const scripts = template.content.querySelectorAll('script');
    const nonScriptNodes: Node[] = [];

    template.content.childNodes.forEach(node => {
      if (node.nodeName !== 'SCRIPT') {
        nonScriptNodes.push(node.cloneNode(true));
      }
    });

    // Insert non-script elements, setting locale on BookingKit v3 widgets
    const locale = LANG_TO_LOCALE[i18n.language] || i18n.language;
    nonScriptNodes.forEach(node => {
      if (node instanceof Element) {
        if (node.tagName.toLowerCase() === 'bk-floating-widget') {
          node.setAttribute('data-locale', locale);
        }
        node.querySelectorAll('bk-floating-widget').forEach(widget => {
          widget.setAttribute('data-locale', locale);
        });
      }
      container.appendChild(node);
    });

    // Create and append script elements so they execute
    scripts.forEach(originalScript => {
      const script = document.createElement('script');
      Array.from(originalScript.attributes).forEach(attr => {
        script.setAttribute(attr.name, attr.value);
      });
      if (originalScript.textContent) {
        script.textContent = originalScript.textContent;
      }
      container.appendChild(script);
    });

    // The BookingKit widget creates overlay/modal elements dynamically in the DOM.
    // Lenis (smooth scroll) intercepts wheel events globally, preventing scrolling
    // inside the widget. We observe the DOM for BookingKit elements and add
    // data-lenis-prevent so Lenis ignores scroll events within them.
    const processedElements = new WeakSet<Element>();

    const observer = new MutationObserver(() => {
      document.querySelectorAll(BK_SELECTOR).forEach(el => {
        if (!processedElements.has(el)) {
          processedElements.add(el);
          el.setAttribute('data-lenis-prevent', '');
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      container.innerHTML = '';
    };
  }, [embedCode]);

  return <div ref={containerRef} />;
};

export default FloatingWidget;
